import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink as RRNavLink } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { push } from "react-router-redux";
import GoogleTranslate from "../components/GoogleTranslate"; // keep your path
import CommandPalette from "../components/CommandPalette";
import Breadcrumbs from "../components/Breadcrumbs";
import EnhancedTooltip from "../components/EnhancedTooltip";
import DarkModeToggle from "../components/DarkModeToggle";
import { ToastProvider } from "../components/ToastProvider";
import Minesweeper from "../components/Minesweeper";
import { authLogout } from "../actions/auth";
import { fetchMachinesNightscan } from "../actions/machines";
import { fetchOpenTicketsCount } from "../actions/supporttickets";
import { fetchOpenIncidencesCount } from "../actions/incidences";
import { canAccess, hasAccessToAny, isFeatureLocked } from "../utils/permissions";
import FloatingToolsButton from '../components/FloatingToolsButton';

// lucide-react icons
import {
  LayoutGrid,
  Menu as MenuIcon,
  LogOut,
  User as UserIcon,
  Hotel,
  CreditCard,
  Unlock,
  Receipt,
  Monitor,
  QrCode,
  Smartphone,
  Route,
  Info,
  Database,
  Wifi,
  DoorOpen,
  Lock,
  CalendarCheck2,
  PencilRuler,
  Newspaper,
  Gem,
  Download,
  Filter,
  Star,
  BarChart2,
  PieChart,
  Calculator,
  Target,
  HelpCircle,
  BookOpen,
  MessageSquare,
  Crown,
  FileSignature,
  Gamepad2,
  SlidersHorizontal,
  UserCog,
  ArrowRight,
  ArrowLeft,
  Key,
  KeyRound,
  KeySquare,
  Search,
  Headset,
  Mountain,
  Mail,
  Tag,
  Ticket,
  AlertTriangle,
  ShieldCheck
} from "lucide-react";

/**
 * Lightweight styles. Inject once on mount.
 */
const injectStyles = () => {
  if (document.getElementById("app-shell-styles")) return;
  const css = `
/* ==================== VARIABLES ==================== */
:root {
  --vw-dark: #0f172a;
  --vw-dark-900: #0b1223;
  --vw-dark-gradient: linear-gradient(to bottom, #0f172a, #01315f);
  --vw-accent: #1e293b;
  --sidebar-w: 280px;
  --sidebar-collapsed-w: 72px;
  --sidebar-icons-w: 72px;
  --sidebar-sub-w: 260px;
  --topbar-h: 64px;
  --mobile-bottom-nav-h: 70px;
  --danger: #ef4444;
  --focus-ring: 0 0 0 3px rgba(59, 130, 246, 0.5);
  --focus-ring-light: 0 0 0 3px rgba(255, 255, 255, 0.3);
  
  /* Theme colors - Light mode (default) */
  --bg-primary: #f7f8fb;
  --bg-secondary: #ffffff;
  --text-primary: #111827;
  --text-secondary: #6b7280;
  --border-color: #e5e7eb;
  
  /* Topbar colors - Light mode */
  --topbar-bg: #ffffff;
  --topbar-text: #111827;
  --topbar-border: #e5e7eb;
  --topbar-btn-border: rgba(0, 0, 0, 0.1);
  --topbar-btn-hover: rgba(0, 0, 0, 0.05);
  --topbar-icon-color: #6b7280;
  
  /* Sidebar colors - Light mode */
  --sidebar-bg: #ffffff;
  --sidebar-icons-bg: #f8f9fa;
  --sidebar-border: #e5e7eb;
  --sidebar-text: #111827;
  --sidebar-text-secondary: #6b7280;
  --sidebar-icon-color: #6b7280;
  --sidebar-icon-active: #111827;
  --sidebar-btn-hover: rgba(0, 0, 0, 0.05);
  --sidebar-btn-active: rgba(0, 0, 0, 0.08);
  --sidebar-submenu-bg: #ffffff;
  --sidebar-submenu-label: #6b7280;
  --sidebar-submenu-border: #e5e7eb;
}

/* Dark mode variables */
.dark-mode {
  --bg-primary: #0f172a;
  --bg-secondary: #1e293b;
  --text-primary: #f9fafb;
  --text-secondary: #d1d5db;
  --border-color: #374151;
  --dark-gradient: linear-gradient(135deg,#022040,#0a4080);
  
  /* Topbar colors - Dark mode */
  --topbar-bg: rgba(15, 23, 42, 0.85);
  --topbar-text: #ffffff;
  --topbar-border: rgba(255, 255, 255, 0.08);
  --topbar-btn-border: rgba(255, 255, 255, 0.2);
  --topbar-btn-hover: rgba(255, 255, 255, 0.1);
  --topbar-icon-color: #ffffff;
  
  /* Sidebar colors - Dark mode */
  --sidebar-bg: rgba(15, 23, 42, 0.90);
  --sidebar-icons-bg: rgba(15, 23, 42, 0.95);
  --sidebar-border: rgba(255, 255, 255, 0.09);
  --sidebar-text: #ffffff;
  --sidebar-text-secondary: #cbd5e1;
  --sidebar-icon-color: #9ca3af;
  --sidebar-icon-active: #ffffff;
  --sidebar-btn-hover: rgba(255, 255, 255, 0.1);
  --sidebar-btn-active: rgba(255, 255, 255, 0.08);
  --sidebar-submenu-bg: linear-gradient(to bottom, #0f172a, #01315f);
  --sidebar-submenu-label: #9ca3af;
  --sidebar-submenu-border: rgba(255, 255, 255, 0.05);
}

.dark-mode .shell {
  background-color: #f7f8fb;
}

.dark-mode .breadcrumbs {
  background: var(--dark-gradient);
  border-color: var(--border-color);
}

.dark-mode .breadcrumbs-link,
.dark-mode .breadcrumbs-text {
  color: var(--text-secondary);
}

.dark-mode .breadcrumbs-current {
  color: var(--text-primary);
}

.dark-mode .command-palette {
  background: var(--dark-gradient);
  border: 1px solid var(--border-color);
}

.dark-mode .command-palette-input {
  color: var(--text-primary);
}

.dark-mode .command-palette-input::placeholder {
  color: var(--text-secondary);
}

.dark-mode .command-palette-item-label {
  color: var(--text-primary);
}

.dark-mode .command-palette-item-description {
  color: var(--text-secondary);
}

.dark-mode .command-palette-item:hover,
.dark-mode .command-palette-item.selected {
  background: rgba(255, 255, 255, 0.05);
}

.dark-mode .toast-item {
  background: var(--dark-gradient);
  border-color: var(--border-color);
}

.dark-mode .toast-title {
  color: var(--text-primary);
}

/* ==================== TOPBAR ==================== */
.topbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  height: var(--topbar-h);
  background: var(--dark-gradient);
  color: #fff;
  display: flex;
  align-items: center;
  z-index: 1030;
  box-shadow: 0 2px 8px rgba(0,0,0,.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.topbar__inner {
  width: 100%;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.topbar__left, .topbar__right {
  display: flex;
  align-items: center;
}

.topbar__brand img {
  height: 30px;
  display: block;
}

/* Hide dark logo in light mode, show light logo */
.topbar__brand .logo-dark {
  display: none;
}

.topbar__brand .logo-light {
  display: block;
}

/* Hide light logo in dark mode, show dark logo */
.dark-mode .topbar__brand .logo-dark {
  display: block;
}

.dark-mode .topbar__brand .logo-light {
  display: none;
}

.topbar__brand:focus {
  outline: none;
  box-shadow: var(--focus-ring);
  border-radius: 8px;
}

.btn-ghost {
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  gap: 6px;
}
.btn-ghost:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}
.btn-ghost:active {
  transform: translateY(0);
}
.btn-ghost:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.nightscan-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.nightscan-btn:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}
.nightscan-btn:active {
  transform: translateY(0);
}
.nightscan-btn:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.nightscan-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--danger);
  color: #fff;
  font-size: 11px;
  border-radius: 999px;
  line-height: 1;
}

/* Tickets indicator button */
.tickets-indicator-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.tickets-indicator-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
  transform: translateY(-1px);
}
.tickets-indicator-btn:active {
  transform: translateY(0);
}
.tickets-indicator-btn .indicator-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #3b82f6;
  color: #fff;
  font-size: 11px;
  border-radius: 999px;
  line-height: 1;
}

/* Incidences indicator button */
.incidences-indicator-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  color: var(--topbar-text);
  padding: 6px 10px;
  border-radius: 10px;
  margin-right: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.incidences-indicator-btn:hover {
  background: rgba(249, 115, 22, 0.1);
  border-color: #f97316;
  transform: translateY(-1px);
}
.incidences-indicator-btn:active {
  transform: translateY(0);
}
.incidences-indicator-btn .indicator-badge {
  position: absolute;
  top: -6px; right: -6px;
  min-width: 18px; height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f97316;
  color: #fff;
  font-size: 11px;
  border-radius: 999px;
  line-height: 1;
}

.dropdown-toggle:after {
  color: var(--topbar-text) !important;
}

/* Language dropdown styling */
.nav-item.dropdown {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
}

.dropdown-toggle {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px !important;
}

.dropdown-toggle::after {
  margin-left: 4px !important;
}

/* Mobile-specific topbar */
.topbar__mobileToggle { display: none; }
@media (max-width: 768px) {
  .topbar__right .btn-ghost.user-btn,
  .topbar__right .btn-ghost.logout-btn { display: none; }
  /* Hide nightscan, tickets, incidences buttons on mobile - they're in Profile menu */
  .nightscan-btn,
  .tickets-indicator-btn,
  .incidences-indicator-btn { display: none !important; }
  .topbar__mobileToggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 1px solid var(--topbar-btn-border);
    color: var(--topbar-text);
    padding: 6px 10px;
    border-radius: 10px;
    margin-left: 8px;
  }
  .topbar__mobileToggle:hover { background: var(--topbar-btn-hover); }
}

/* Hide list markers in dropdowns */
.dropdown-menu li::marker,
.nav-item::marker {
  display: none;
  content: none;
}

/* Ensure dropdowns appear above other elements */
.dropdown-menu {
  z-index: 1050;
}

.nav-item {
  list-style: none;
}

/* ==================== LAYOUT ==================== */
.shell {
  display: flex;
  min-height: 100vh;
  background: #f7f8fb;
}

/* ==================== SIDEBAR PRO ==================== */
.sidebar-pro {
  position: fixed;
  top: var(--topbar-h);
  left: 0;
  bottom: 0;
  display: flex;
  background: var(--sidebar-bg);
  color: var(--sidebar-text);
  z-index: 1020;
  box-shadow: 2px 0 8px rgba(0,0,0,.15);
  transition: all .25s ease;
}

/* Left icons column */
.sidebar-icons {
  width: var(--sidebar-icons-w);
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--dark-gradient);
  border-right: 1px solid var(--sidebar-border);
  padding-top: 10px;
}

.icon-btn {
  background: transparent;
  color: var(--sidebar-icon-color);
  border: none;
  padding: 14px 0;
  cursor: pointer;
  width: 100%;
  min-height: 72px;
  display: flex;
  justify-content: center;
  transition: all .2s ease;
  outline: none;
  flex-direction: column;
  align-items: center;
  position: relative;
}
.icon-btn:hover, .icon-btn.active {
  color: var(--sidebar-icon-active);
  background: var(--sidebar-btn-hover);
}

.icon-btn:focus {
  outline: none;
  box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.5);
}

.icon-btn:focus:not(:focus-visible) {
  box-shadow: none;
}

.icon-label {
  font-size: 0.7rem;
  margin-top: 0.25rem;
}

/* Right submenu panel */
.submenu-panel {
  width: 0;
  overflow: hidden;
  background: var(--dark-gradient);
  transition: width .25s ease;
}
.submenu-panel.open { width: var(--sidebar-sub-w); }

.submenu-content {
  opacity: 0;
  transform: translateX(-10px);
  transition: opacity .25s ease, transform .25s ease;
  padding: 16px 12px;
  height: 100%;
  overflow-y: auto;
}
.submenu-panel.open .submenu-content {
  opacity: 1;
  transform: translateX(0);
}

/* Submenu links */
.submenu-label {
  font-size: 11px;
  text-transform: uppercase;
  color: var(--sidebar-submenu-label);
  padding: 8px 12px 4px;
  margin-top: 8px;
  letter-spacing: .05em;
  border-bottom: 1px solid var(--sidebar-submenu-border);
}

.submenu-content .nav-button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  color: var(--sidebar-text-secondary);
  background: transparent;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none;
  transition: background .12s ease, color .12s ease;
  border: none;
  cursor: pointer;
  text-align: left;
  margin-top: 5px;
}
.submenu-content .nav-button:hover,
.submenu-content .nav-button.active {
  background: var(--sidebar-btn-active);
  color: var(--sidebar-text);
  transform: translateX(4px);
}

.submenu-content .nav-button:active {
  transform: translateX(2px);
}
.submenu-content .nav-button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
.submenu-content .nav-button svg {
  min-width: 20px;
  opacity: 0.9;
}

/* ── Pro-locked menu items ── */
.nav-button--locked {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  color: var(--sidebar-text-secondary);
  background: transparent;
  padding: 10px 12px;
  border-radius: 10px;
  text-decoration: none;
  border: none;
  cursor: not-allowed;
  text-align: left;
  margin-top: 5px;
  opacity: 0.5;
  position: relative;
}
.nav-button--locked:hover {
  opacity: 0.7;
}
.nav-button--locked svg {
  min-width: 20px;
  opacity: 0.6;
}

.pro-badge-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: #fff;
  padding: 1px 5px;
  border-radius: 4px;
  margin-left: auto;
  line-height: 1.3;
  text-transform: uppercase;
  flex-shrink: 0;
}

.lock-icon-inline {
  opacity: 0.6;
  margin-left: 4px;
  flex-shrink: 0;
}

/* Pro License Toast Notification */
.pro-toast {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: #fff;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  z-index: 10000;
  font-size: 14px;
  animation: proToastSlideUp 0.3s ease-out;
  border: 1px solid rgba(245,158,11,0.3);
  white-space: nowrap;
}
.pro-toast svg {
  color: #f59e0b;
  flex-shrink: 0;
}
.pro-toast strong {
  color: #f59e0b;
}
.pro-toast--mobile {
  bottom: 80px;
}

@keyframes proToastSlideUp {
  from { opacity: 0; transform: translateX(-50%) translateY(16px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* Scrollbars for submenu */
.submenu-content::-webkit-scrollbar { width: 8px; }
.submenu-content::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.25);
  border-radius: 999px;
}
.submenu-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255,255,255,0.5);
}

/* Content shifting (desktop only) */
.content.shifted {
  margin-left: calc(var(--sidebar-icons-w) + var(--sidebar-sub-w));
  max-width: calc(100vw - (var(--sidebar-icons-w) + var(--sidebar-sub-w)));
}
.content.not-shifted {
  margin-left: var(--sidebar-icons-w);
  max-width: calc(100vw - var(--sidebar-icons-w));
}
.content {
  flex: 1;
  padding-top: var(--topbar-h);
  transition: margin-left .25s ease;
  max-width: calc(100vw - var(--sidebar-icons-w));
}

/* ==================== NIGHTSCAN RIGHT DRAWER ==================== */
.rightpanel {
  position: fixed;
  top: var(--topbar-h);
  right: 0;
  bottom: 0;
  width: 360px;
  max-width: 92vw;
  background: #fff;
  color: #0f172a;
  border-left: 1px solid rgba(0,0,0,.1);
  box-shadow: -2px 0 12px rgba(0,0,0,.12);
  transform: translateX(100%);
  transition: transform .22s ease;
  z-index: 1040;
  display: flex;
  flex-direction: column;
}
.rightpanel--open { transform: translateX(0); }
.rightpanel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  background: #f8fafc;
}
.rightpanel__title { font-weight: 600; }
.rightpanel__body { padding: 12px; overflow-y: auto; }
.rightpanel__item {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(0,0,0,.08);
  background: #fff;
  border-radius: 10px;
  padding: 8px 10px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all .15s ease;
}
.rightpanel__item:hover {
  border-color: #0015ce;
  background: #0312e71a;
  transform: translateX(-4px);
  box-shadow: 0 2px 8px rgba(0, 21, 206, 0.15);
}

.rightpanel__item:active {
  transform: translateX(-2px);
}
.status-badge {
  display: inline-block;
  color: #fff;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
}
.rightpanel__empty {
  padding: 24px;
  color: #64748b;
  text-align: center;
}
.backdrop-right {
  position: fixed;
  top: var(--topbar-h);
  left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,.45);
  z-index: 1035;
}

/* ==================== MOBILE BOTTOM TAB BAR ==================== */
@media (max-width: 768px) {
  .mobile-bottom-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: var(--mobile-bottom-nav-h);
    background: var(--sidebar-bg);
    border-top: 1px solid var(--sidebar-border);
    display: flex;
    align-items: center;
    justify-content: space-around;
    z-index: 1030;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    padding: 0 8px;
    opacity: 1;
  }

  .dark-mode .mobile-bottom-nav {
    background: var(--dark-gradient);
    backdrop-filter: blur(10px);
  }

  .mobile-tab {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: transparent;
    border: none;
    color: var(--sidebar-icon-color);
    padding: 8px 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 8px;
    position: relative;
    min-width: 0;
  }

  .mobile-tab:active {
    transform: scale(0.95);
  }

  .mobile-tab.active {
    color: var(--sidebar-icon-active);
    background: var(--sidebar-btn-active);
  }

  .mobile-tab-label {
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .mobile-tab-badge {
    position: absolute;
    top: 6px;
    right: 8px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--danger);
    color: #fff;
    font-size: 10px;
    border-radius: 999px;
    line-height: 1;
  }

  /* Mobile menu modal (slides from bottom) */
  .mobile-menu-modal {
    position: fixed;
    bottom: var(--mobile-bottom-nav-h);
    left: 0;
    right: 0;
    max-height: calc(85vh - var(--mobile-bottom-nav-h));
    background: var(--dark-gradient);
    border-radius: 20px 20px 0 0;
    z-index: 1029;
    transform: translateY(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
  }

  .mobile-menu-modal.open {
    transform: translateY(0);
  }

  .mobile-menu-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--sidebar-border);
    background: var(--dark-gradient);
    border-radius: 20px 20px 0 0;
  }

  .mobile-menu-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--sidebar-text);
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .mobile-menu-close {
    background: transparent;
    border: none;
    color: var(--sidebar-icon-color);
    cursor: pointer;
    padding: 8px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
  }

  .mobile-menu-close:hover {
    background: var(--sidebar-btn-hover);
  }

  .mobile-menu-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: visible;
    padding: 16px;
  }

  /* Language dropdown in mobile needs proper positioning */
  @media (max-width: 768px) {
    .mobile-menu-content .dropdown-menu {
      position: absolute !important;
      top: auto !important;
      bottom: 100% !important;
      left: 50% !important;
      right: auto !important;
      transform: translateX(-50%) translateY(-8px) !important;
      margin: 0 !important;
      z-index: 9999 !important;
      max-height: 300px;
      overflow-y: auto;
      padding: 10rem .5rem;
    }

    .mobile-menu-content .dropdown {
      position: relative !important;
    }
  }

  .mobile-menu-section {
    margin-bottom: 20px;
  }

  .mobile-menu-section-title {
    font-size: 11px;
    text-transform: uppercase;
    color: var(--sidebar-submenu-label);
    padding: 8px 12px 4px;
    letter-spacing: 0.05em;
    border-bottom: 1px solid var(--sidebar-submenu-border);
    margin-bottom: 8px;
  }

  .mobile-menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    color: var(--sidebar-text-secondary);
    background: transparent;
    padding: 14px 12px;
    border-radius: 10px;
    text-decoration: none;
    transition: all 0.2s ease;
    border: none;
    cursor: pointer;
    text-align: left;
    font-size: 15px;
  }

  .mobile-menu-item:active {
    transform: scale(0.98);
  }

  .mobile-menu-item:hover,
  .mobile-menu-item.active {
    background: var(--sidebar-btn-active);
    color: var(--sidebar-text);
  }

  .mobile-menu-item svg {
    min-width: 22px;
    opacity: 0.9;
  }

  /* Mobile locked items */
  .mobile-menu-item--locked {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .mobile-menu-item--locked:hover {
    background: transparent;
    opacity: 0.65;
  }
  .mobile-menu-item--locked:active {
    transform: none;
  }

  /* Profile menu specific styles */
  .mobile-profile-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    background: var(--dark-gradient);
    border-bottom: 1px solid var(--sidebar-border);
  }

  .mobile-profile-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: var(--sidebar-btn-active);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--sidebar-icon-active);
  }

  .mobile-profile-info {
    flex: 1;
  }

  .mobile-profile-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--sidebar-text);
    margin-bottom: 2px;
  }

  .mobile-profile-role {
    font-size: 13px;
    color: var(--sidebar-text-secondary);
  }

  .mobile-menu-divider {
    height: 1px;
    background: var(--sidebar-border);
    margin: 12px 0;
  }

  .content { 
    margin-left: 0 !important;
    max-width: 100vw !important;
    padding-bottom: calc(var(--mobile-bottom-nav-h) + 20px) !important;
  }

  .backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1028;
    backdrop-filter: blur(4px);
  }

  /* Hide desktop sidebar on mobile */
  .sidebar-pro {
    display: none;
  }
}

/* ==================== REDUCED MOTION ==================== */
@media (prefers-reduced-motion: reduce) {
  .nav-button,
  .submenu-panel,
  .submenu-content,
  .content,
  .sidebar-pro,
  .rightpanel {
    transition: none !important;
  }
}

/* ==================== COMMAND PALETTE ==================== */
.command-palette-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 2000;
  animation: fadeIn 0.15s ease;
}

.command-palette {
  position: fixed;
  top: 15vh;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 640px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  z-index: 2001;
  overflow: hidden;
  animation: slideDown 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideDown {
  from { 
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to { 
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.command-palette-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
}

.command-palette-search-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.command-palette-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #111827;
  background: transparent;
}

.command-palette-input::placeholder {
  color: #9ca3af;
}

.command-palette-kbd {
  padding: 4px 8px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  color: #6b7280;
  font-family: monospace;
  font-weight: 600;
}

.command-palette-content {
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
}

.command-palette-content::-webkit-scrollbar { width: 8px; }
.command-palette-content::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 999px;
}
.command-palette-content::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.command-palette-empty {
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.command-palette-group {
  margin-bottom: 12px;
}

.command-palette-group-label {
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.05em;
}

.command-palette-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s ease;
}

.command-palette-item:hover,
.command-palette-item.selected {
  background: #f3f4f6;
  transform: translateX(2px);
}

.command-palette-item:active {
  transform: scale(0.99);
}

.command-palette-item-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.command-palette-item-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.command-palette-item-content {
  flex: 1;
  min-width: 0;
}

.command-palette-item-label {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin-bottom: 2px;
}

.command-palette-item-description {
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.command-palette-item-arrow {
  color: #9ca3af;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.command-palette-item:hover .command-palette-item-arrow,
.command-palette-item.selected .command-palette-item-arrow {
  opacity: 1;
}

/* Command palette locked items */
.command-palette-item--locked {
  opacity: 0.5;
  cursor: not-allowed;
}
.command-palette-item--locked:hover,
.command-palette-item--locked.selected {
  opacity: 0.65;
}
.pro-badge-cmd {
  display: inline-flex;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: #fff;
  padding: 1px 5px;
  border-radius: 4px;
  margin-left: 8px;
  vertical-align: middle;
  text-transform: uppercase;
}

.command-palette-footer {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 12px;
  color: #6b7280;
}

.command-palette-footer-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.command-palette-footer kbd {
  padding: 2px 6px;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  font-size: 11px;
  font-family: monospace;
  font-weight: 600;
}

/* ==================== BREADCRUMBS ==================== */
.breadcrumbs {
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  padding: 12px 24px;
  position: sticky;
  top: var(--topbar-h);
  z-index: 100;
}

.breadcrumbs-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.breadcrumbs-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.breadcrumbs-link,
.breadcrumbs-text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #6b7280;
  text-decoration: none;
  transition: color 0.15s ease, transform 0.15s ease;
}

.breadcrumbs-link:hover {
  color: #3b82f6;
  transform: translateY(-1px);
}

.dark-mode .breadcrumbs-link:hover {
  color: #60a5fa;
}

.breadcrumbs-link:active {
  transform: translateY(0);
}

.breadcrumbs-link:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
  border-radius: 4px;
}

.breadcrumbs-current {
  color: #111827;
  font-weight: 500;
}

.breadcrumbs-icon {
  flex-shrink: 0;
}

.breadcrumbs-separator {
  color: #d1d5db;
  flex-shrink: 0;
}

/* ==================== ENHANCED TOOLTIP ==================== */
.enhanced-tooltip {
  position: fixed;
  z-index: 10000;
  pointer-events: none;
  animation: tooltipFadeIn 0.15s ease;
}

@keyframes tooltipFadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.enhanced-tooltip-content {
  background: #1f2937;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 280px;
}

.enhanced-tooltip--rich .enhanced-tooltip-content {
  padding: 10px 14px;
}

.enhanced-tooltip-simple {
  white-space: nowrap;
}

.enhanced-tooltip-rich {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.enhanced-tooltip-title {
  font-weight: 600;
  font-size: 13px;
}

.enhanced-tooltip-description {
  font-size: 12px;
  color: #d1d5db;
  line-height: 1.5;
}

.enhanced-tooltip-shortcut {
  display: flex;
  gap: 4px;
  margin-top: 4px;
}

.enhanced-tooltip-kbd {
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 3px;
  font-size: 11px;
  font-family: monospace;
  font-weight: 600;
}

.enhanced-tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
}

.enhanced-tooltip-arrow--top {
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px 6px 0 6px;
  border-color: #1f2937 transparent transparent transparent;
}

.enhanced-tooltip-arrow--bottom {
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 0 6px 6px 6px;
  border-color: transparent transparent #1f2937 transparent;
}

.enhanced-tooltip-arrow--left {
  right: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 0 6px 6px;
  border-color: transparent transparent transparent #1f2937;
}

.enhanced-tooltip-arrow--right {
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  border-width: 6px 6px 6px 0;
  border-color: transparent #1f2937 transparent transparent;
}

/* ==================== TOAST NOTIFICATIONS ==================== */
.toast-container {
  position: fixed;
  top: calc(var(--topbar-h) + 20px);
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  pointer-events: none;
}

.toast-item {
  pointer-events: all;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 320px;
  max-width: 420px;
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px var(--toast-border);
  animation: toastSlideIn 0.3s cubic-bezier(0.21, 1.02, 0.73, 1);
  transition: all 0.2s ease;
}

.toast-item:hover {
  transform: translateX(-4px) scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2), 0 0 0 1px var(--toast-border);
}

@keyframes toastSlideIn {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  margin-bottom: 2px;
}

.toast-message {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.toast-close {
  flex-shrink: 0;
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-close:hover {
  background: #f3f4f6;
  color: #111827;
  transform: scale(1.1);
}

.toast-close:active {
  transform: scale(0.95);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .toast-container {
    right: 12px;
    left: 12px;
    top: calc(var(--topbar-h) + 12px);
  }
  
  .toast-item {
    min-width: 0;
    max-width: 100%;
  }
}

/* ==================== DARK MODE TOGGLE ==================== */
.dark-mode-toggle {
  background: transparent;
  border: 1px solid var(--topbar-btn-border);
  border-radius: 20px;
  padding: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.dark-mode-toggle:hover {
  background: var(--topbar-btn-hover);
  transform: translateY(-1px);
}

.dark-mode-toggle:active {
  transform: translateY(0);
}

.dark-mode-toggle:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.dark-mode-toggle-track {
  width: 44px;
  height: 22px;
  border-radius: 11px;
  background: rgba(0, 0, 0, 0.1);
  position: relative;
  transition: background 0.3s ease;
}

.dark-mode-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fbbf24;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.dark-mode-icon {
  color: #f59e0b;
}

/* Dark mode styles */
.dark-mode .dark-mode-toggle-track {
  background: rgba(59, 130, 246, 0.3);
}

.dark-mode .dark-mode-toggle-thumb {
  left: 24px;
  background: #3b82f6;
}

.dark-mode .dark-mode-icon {
  color: #fff;
}
  `;
  const el = document.createElement("style");
  el.id = "app-shell-styles";
  el.innerHTML = css;
  document.head.appendChild(el);
};


/** Helpers for Nightscan logic (ported from your legacy header) */
const formatNightscanDate = (timestamp) => {
  const year = timestamp?.substring(0, 4);
  const month = (timestamp?.substring(4, 6) ?? "01") - 1;
  const day = timestamp?.substring(6, 8);
  const hour = timestamp?.substring(8, 10);
  const minute = timestamp?.substring(10, 12);
  const second = timestamp?.substring(12, 14);
  return new Date(year, month, day, hour, minute, second);
};

const isNightscanInSleepPeriod = (nightscanDate, sleepStart, sleepEnd) => {

  if (
    !nightscanDate ||
    !(nightscanDate instanceof Date) ||
    isNaN(nightscanDate)
  ) {
    return false;
  }

  const now = new Date();
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);

  const isTodayOrYesterday =
    nightscanDate.toDateString() === today.toDateString() ||
    nightscanDate.toDateString() === yesterday.toDateString();

  if (!isTodayOrYesterday) return false;

  const startHours = Math.floor((sleepStart ?? 0) / 100);
  const startMinutes = (sleepStart ?? 0) % 100;
  const endHours = Math.floor((sleepEnd ?? 0) / 100);
  const endMinutes = (sleepEnd ?? 0) % 100;

  let startDate = new Date(nightscanDate);
  startDate.setHours(startHours, startMinutes, 0, 0);

  let endDate = new Date(nightscanDate);
  endDate.setHours(endHours, endMinutes, 0, 0);

  if (startHours > endHours || (startHours === endHours && startMinutes > endMinutes)) {
    if (nightscanDate.toDateString() === yesterday.toDateString()) {
      startDate = new Date(yesterday);
      startDate.setHours(startHours, startMinutes, 0, 0);
      endDate = new Date(today);
      endDate.setHours(endHours, endMinutes, 0, 0);
    } else if (nightscanDate.toDateString() === today.toDateString()) {
      const previousDayStartDate = new Date(yesterday);
      previousDayStartDate.setHours(startHours, startMinutes, 0, 0);
      return nightscanDate >= previousDayStartDate && nightscanDate <= endDate;
    }
  }

  return nightscanDate >= startDate && nightscanDate <= endDate;
};

const getMachineStatus = (status, signal) => {
  if (!status) return "UNKNOWN";
  const now = Date.now();
  const lastSignalTime = Date.parse(signal);
  const diffInSeconds = (now - lastSignalTime) / 1000;
  if (diffInSeconds >= 60 * 5) return "OFFLINE";

  const map = {
    OPERATIVE: "ONLINE",
    SLEEP: "SLEEP",
    NIGHT_SCAN: "NIGHTSCAN",
    MAINTENANCE: "MANTENIMIENTO",
    LOCKERS_FULL: "LOCKERS LLENOS",
    TICKET_EMPTY: "SIN TICKET",
    OPERATIVE_SLEEP: "SLEEP ONLINE",
    MAINTENANCE_SLEEP: "SLEEP MAINTENANCE",
    KERONG_OTA: "OTA KERONG",
    BOOTLOADER_OTA: "OTA BOOTLOADER",
    BLE_OFF: "POWER DOWN",
  };
  return map[status] || "UNKNOWN";
};

const getMachineStatusColor = (status, signal) => {
  const now = Date.now();
  const lastSignalTime = Date.parse(signal);
  const diffInSeconds = (now - lastSignalTime) / 1000;
  if (diffInSeconds >= 60 * 5) return "#727370"; // Offline gray

  const colors = {
    OPERATIVE: "#28a745",
    SLEEP: "#0066ff",
    NIGHT_SCAN: "#26c4f6",
    MAINTENANCE: "#ff7b25",
    LOCKERS_FULL: "#ff0000",
    TICKET_EMPTY: "#ff0000",
    OPERATIVE_SLEEP: "#28a745",
    MAINTENANCE_SLEEP: "#ff7b25",
    KERONG_OTA: "#000000",
    BOOTLOADER_OTA: "#000000",
    BLE_OFF: "#288BA8",
    "FULL QR": "#800080",
  };
  return colors[status] || "#727370";
};

/**
 * Top bar
 * - Desktop: translator + user + logout, plus NEW Nightscan toggle button (before language).
 * - Mobile: user/logout hidden, but Nightscan toggle remains visible.
 */
const TopBar = ({
  fullname,
  onLogout,
  isMobile,
  mobileOpen,
  setMobileOpen,
  role,
  type,
  category,
  nightscanCount,
  openTicketsCount,
  openIncidencesCount,
  onToggleNightscan,
  onToggleSidebarMobile,
  onOpenCommandPalette,
  onUserClick,
  push,
}) => {
  const showAdminNightscan = canAccess(role, type, category, 'nightscan');
  
  // Show tickets indicator for MASTER and SAT1
  const showTicketsIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT1');
  // Show incidences indicator for MASTER and SAT2
  const showIncidencesIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT2');

  return (
    <div className="topbar">
      <div className="topbar__inner">
        <div className="topbar__left">
          <Link to="/" className="topbar__brand mr-3" aria-label="Home">
            <img src="/images/logo_white.png" alt="Logo" className="logo-dark" />
            <img src="/images/logo_blue.png" alt="Logo" className="logo-light" />
          </Link>
          <span style={{ color: 'var(--topbar-text)', fontWeight: 600, fontSize: 14 }}>LOCKERS</span>
        </div>

        <div className="topbar__right">
          {/* Command Palette trigger */}
          <EnhancedTooltip
            content="Búsqueda rápida"
            description="Buscar y navegar rápidamente"
            shortcut={['⌘K']}
            position="bottom"
          >
            <button
              className="btn-ghost"
              onClick={onOpenCommandPalette}
              title="Búsqueda rápida"
              aria-label="Abrir búsqueda rápida (Cmd+K)"
              style={{ marginRight: 8 }}
            >
              <Search size={18} />
              {!isMobile && <span style={{ marginLeft: 6 }}>Buscar</span>}
            </button>
          </EnhancedTooltip>

          {/* Nightscan toggle (before language) */}
          {showAdminNightscan && (
            <EnhancedTooltip
              content="Incidencias Nightscan"
              description="Ver máquinas con incidencias de escaneo nocturno"
              position="bottom"
            >
              <button
                className="nightscan-btn"
                onClick={onToggleNightscan}
                title="Incidencias Nightscan"
                aria-label="Abrir/cerrar Nightscan"
              >
                <Info size={18} />
                {!isMobile && <span>Nightscan</span>}
                {!!nightscanCount && <span className="nightscan-badge">{nightscanCount}</span>}
              </button>
            </EnhancedTooltip>
          )}

          {/* Tickets indicator - MASTER and SAT1 only */}
          {showTicketsIndicator && (
            <EnhancedTooltip
              content="Tickets Abiertos"
              description="Ver tickets de soporte abiertos"
              position="bottom"
            >
              <button
                className="tickets-indicator-btn"
                onClick={() => push('/supporttickets')}
                title="Tickets Abiertos"
                aria-label="Ver tickets abiertos"
              >
                <Ticket size={18} />
                {!isMobile && <span>Tickets</span>}
                {openTicketsCount > 0 && <span className="indicator-badge">{openTicketsCount}</span>}
              </button>
            </EnhancedTooltip>
          )}

          {/* Incidences indicator - MASTER and SAT2 only */}
          {showIncidencesIndicator && (
            <EnhancedTooltip
              content="Incidencias Abiertas"
              description="Ver incidencias abiertas"
              position="bottom"
            >
              <button
                className="incidences-indicator-btn"
                onClick={() => push('/incidences')}
                title="Incidencias Abiertas"
                aria-label="Ver incidencias abiertas"
              >
                <AlertTriangle size={18} />
                {!isMobile && <span>Incidencias</span>}
                {openIncidencesCount > 0 && <span className="indicator-badge">{openIncidencesCount}</span>}
              </button>
            </EnhancedTooltip>
          )}

          <div className="mr-2 d-none d-md-flex"><GoogleTranslate /></div>

          {/* Dark Mode Toggle */}
          {!isMobile && (
            <div style={{ marginRight: 8 }}>
              <EnhancedTooltip
                content="Tema"
                description="Cambiar entre modo claro y oscuro"
                position="bottom"
              >
                <DarkModeToggle />
              </EnhancedTooltip>
            </div>
          )}

          {/* Desktop-only actions */}
          {!isMobile && (
            <>
              <EnhancedTooltip content={fullname} position="bottom">
                <button type="button" className="btn-ghost user-btn" title={fullname} onClick={onUserClick}>
                  {role === 'ADMIN' ? (
                    <img
                      src={`https://vacway-images.s3.eu-central-1.amazonaws.com/${fullname}.png`}
                      alt={fullname}
                      style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'cover', marginRight: 6 }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://vacway-images.s3.eu-central-1.amazonaws.com/default.png";
                      }}
                    />
                  ) : (
                    <UserIcon size={18} />
                  )}
                  <span className="d-none d-sm-inline">{fullname}</span>
                </button>
              </EnhancedTooltip>
              <EnhancedTooltip content="Cerrar sesión" position="bottom">
                <button className="btn-ghost logout-btn" onClick={onLogout} style={{ marginLeft: 8 }}>
                  <LogOut size={18} />
                  <span className="d-none d-sm-inline">Salir</span>
                </button>
              </EnhancedTooltip>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Sidebar items
 */
const Group = ({ label, children }) => (
  <div className="nav-group">
    <div className="nav-group__label">{label}</div>
    {children}
  </div>
);

const Collapsible = ({ icon: Icon, label, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="nav-item">
      <button className="nav-button" onClick={() => setOpen(!open)}>
        <span className="nav-button__icon"><Icon size={18} /></span>
        <span className="nav-button__label">{label}</span>
        {open ? <ChevronDown size={16} className="nav-button__chev" /> : <ChevronRight size={16} className="nav-button__chev" />}
      </button>
      {open && <div className="subnav">{children}</div>}
    </div>
  );
};

/**
 * Sidebar (desktop collapsible + mobile drawer)
 */
const Sidebar = ({ role, type, category, onNavigate, activeMenu, setActiveMenu }) => {
  const toggleMenu = (menu) => setActiveMenu(prev => (prev === menu ? null : menu));

  // Pro-lock toast state
  const [proToast, setProToast] = React.useState(false);
  const proToastTimer = React.useRef(null);
  const showProToast = () => {
    setProToast(true);
    if (proToastTimer.current) clearTimeout(proToastTimer.current);
    proToastTimer.current = setTimeout(() => setProToast(false), 3500);
  };

  // Check if user has access to any feature in each category
  const hasAcciones = hasAccessToAny(role, type, category, ['montarto', 'lockersale', 'pin', 'supportticketadd']);
  const hasCanales = hasAccessToAny(role, type, category, ['machines', 'qrs', 'pdas']);
  const hasGestion = hasAccessToAny(role, type, category, ['lockersale', 'pin', 'redeemcodes', 'rentals', 'calendars']);
  const hasReporte = hasAccessToAny(role, type, category, ['sales', 'internalsales', 'arqueos', 'arqueossales', 'ocupation', 'objectives', 'ocupationaccumulated', 'ocupationdaily', 'pinrecovers']);
  const hasOperativa = hasAccessToAny(role, type, category, ['customers', 'contracts', 'withdrawals', 'audit', 'tpvs', 'modules', 'matrixs', 'lowbluetooth', 'lockersqty', 'openings']);
  const hasSAT = hasAccessToAny(role, type, category, ['supporttickets', 'incidences', 'faults', 'replacements', 'slas']);
  const hasBaqueira = hasAccessToAny(role, type, category, ['operators', 'rules', 'articles', 'arqueosbaqueira', 'montarto']);
  const hasSoporte = hasAccessToAny(role, type, category, ['supportticketadd', 'faqs', 'manuals', 'support-chat']);
  const hasAdmin = hasAccessToAny(role, type, category, ['users', 'gameranking']);

  const NavLinkItem = ({ to, icon: Icon, children }) => {
    const feature = to.replace(/^\//, '');
    const locked = isFeatureLocked(role, type, category, feature);

    if (locked) {
      return (
        <div
          className="nav-button nav-button--locked"
          onClick={(e) => { e.preventDefault(); showProToast(); }}
          role="button"
          tabIndex={0}
        >
          {Icon && <Icon size={16} />}
          <span>{children}</span>
          <span className="pro-badge-inline">PRO</span>
          <Lock size={12} className="lock-icon-inline" />
        </div>
      );
    }

    return (
      <RRNavLink
        to={to}
        className="nav-button"
        onClick={() => {
          setActiveMenu(null);
          onNavigate();
        }}
      >
        {Icon && <Icon size={16} />}
        <span>{children}</span>
      </RRNavLink>
    );
  };

  return (
    <div className="sidebar-pro">
      {/* Left icons column */}
      <div className="sidebar-icons">
        {hasCanales && (
          <EnhancedTooltip
            content="Canales"
            description="Gestión de canales de venta"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "canales" ? "active" : ""}`}
              onClick={() => toggleMenu("canales")}
              title="Canales"
              aria-label="Menú de canales"
            >
              <Monitor size={20} />
              <span className="icon-label">Canales</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasGestion && (
          <EnhancedTooltip
            content="Gestión"
            description="Gestión de ventas, reservas y calendarios"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "gestion" ? "active" : ""}`}
              onClick={() => toggleMenu("gestion")}
              title="Gestión"
              aria-label="Menú de gestión"
            >
              <CalendarCheck2 size={20} />
              <span className="icon-label">Gestión</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasReporte && (
          <EnhancedTooltip
            content="Reporte"
            description="Reportes y análisis de datos"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "reporte" ? "active" : ""}`}
              onClick={() => toggleMenu("reporte")}
              title="Reporte"
              aria-label="Menú de reportes"
            >
              <BarChart2 size={20} />
              <span className="icon-label">Reporte</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasOperativa && (
          <EnhancedTooltip
            content="Operativa"
            description="Gestión operativa del sistema"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "operativa" ? "active" : ""}`}
              onClick={() => toggleMenu("operativa")}
              title="Operativa"
              aria-label="Menú de operativa"
            >
              <SlidersHorizontal size={20} />
              <span className="icon-label">Operativa</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasSAT && (
          <EnhancedTooltip
            content="SAT"
            description="Servicio de atención técnica"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "sat" ? "active" : ""}`}
              onClick={() => toggleMenu("sat")}
              title="SAT"
              aria-label="Menú de SAT"
            >
              <Headset size={20} />
              <span className="icon-label">SAT</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasBaqueira && (
          <EnhancedTooltip
            content="Baqueira"
            description="Operaciones específicas de Baqueira"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "baqueira" ? "active" : ""}`}
              onClick={() => toggleMenu("baqueira")}
              title="Baqueira"
              aria-label="Menú de Baqueira"
            >
              <Mountain size={20} />
              <span className="icon-label">Baqueira</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasSoporte && (
          <EnhancedTooltip
            content="Soporte"
            description="Recursos de ayuda y soporte"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "soporte" ? "active" : ""}`}
              onClick={() => toggleMenu("soporte")}
              title="Soporte"
              aria-label="Menú de soporte"
            >
              <HelpCircle size={20} />
              <span className="icon-label">Soporte</span>
            </button>
          </EnhancedTooltip>
        )}

        {hasAdmin && (
          <EnhancedTooltip
            content="Administración"
            description="Configuración y administración del sistema"
            position="right"
            delay={300}
          >
            <button
              className={`icon-btn ${activeMenu === "admin" ? "active" : ""}`}
              onClick={() => toggleMenu("admin")}
              title="Administración"
              aria-label="Menú de administración"
            >
              <Crown size={20} />
              <span className="icon-label">Admin</span>
            </button>
          </EnhancedTooltip>
        )}
      </div>

      {/* Right submenu panel */}
      <div className={`submenu-panel ${activeMenu ? "open" : ""}`}>
        {activeMenu === "canales" && (
          <div className="submenu-content">
            <div className="submenu-label">Canales de venta</div>
            {canAccess(role, type, category, 'machines') && (
              <NavLinkItem to="/machines" icon={Monitor}>{category === 'BAQUEIRA' ? 'Controladores' : 'Kioscos'}</NavLinkItem>
            )}
            {canAccess(role, type, category, 'qrs') && (
              <NavLinkItem to="/qrs" icon={QrCode}>QRs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'pdas') && (
              <NavLinkItem to="/pdas" icon={Smartphone}>PDAs</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "gestion" && (
          <div className="submenu-content">
            <div className="submenu-label">Gestión</div>
            {canAccess(role, type, category, 'lockersale') && (
              <NavLinkItem to="/lockersale" icon={CreditCard}>Vender</NavLinkItem>
            )}
            {canAccess(role, type, category, 'pin') && (
              <NavLinkItem to="/pin" icon={Unlock}>Recuperar Pin</NavLinkItem>
            )}
            {canAccess(role, type, category, 'redeemcodes') && (
              <NavLinkItem to="/redeemcodes" icon={Ticket}>Códigos Canjeables</NavLinkItem>
            )}
            {canAccess(role, type, category, 'rentals') && (
              <NavLinkItem to="/rentals" icon={Key}>Reservas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'calendars') && (
              <NavLinkItem to="/calendars" icon={CalendarCheck2}>Calendarios</NavLinkItem>
            )}
            {canAccess(role, type, category, 'email-templates') && (
              <NavLinkItem to="/email-templates" icon={Mail}>Plantillas Email</NavLinkItem>
            )}
            {canAccess(role, type, category, 'partners') && (
              <NavLinkItem to="/partners" icon={UserCog}>Partners</NavLinkItem>
            )}
            {canAccess(role, type, category, 'discount-codes') && (
              <NavLinkItem to="/discount-codes" icon={Tag}>Códigos Descuento</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "reporte" && (
          <div className="submenu-content">
            <div className="submenu-label">Reporte</div>
            {canAccess(role, type, category, 'sales') && (
              <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'internalsales') && (
              <NavLinkItem to="/internalsales" icon={Star}>Análisis por tipo</NavLinkItem>
            )}
            {canAccess(role, type, category, 'arqueos') && (
              <>
                <NavLinkItem to="/arqueos" icon={BarChart2}>Gráficos</NavLinkItem>
                <NavLinkItem to="/arqueossales" icon={PieChart}>KPI</NavLinkItem>
              </>
            )}
            {canAccess(role, type, category, 'ocupation') && (
              <NavLinkItem to="/ocupation" icon={BarChart2}>Ocupación</NavLinkItem>
            )}
            {canAccess(role, type, category, 'objectives') && (
              <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'ocupationaccumulated') && (
              <>
                <NavLinkItem to="/ocupationaccumulated" icon={BarChart2}>Ocupación acumulada</NavLinkItem>
                <NavLinkItem to="/ocupationdaily" icon={BarChart2}>Ocupación diaria</NavLinkItem>
              </>
            )}
            {canAccess(role, type, category, 'pinrecovers') && (
              <NavLinkItem to="/pinrecovers" icon={Lock}>Recuperación pines</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "operativa" && (
          <div className="submenu-content">
            <div className="submenu-label">Operativa</div>
            {canAccess(role, type, category, 'customers') && (
              <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
            )}
            {canAccess(role, type, category, 'contracts') && (
              <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'withdrawals') && (
              <NavLinkItem to="/withdrawals" icon={Calculator}>Retiros</NavLinkItem>
            )}
            {canAccess(role, type, category, 'audit') && (
              <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
            )}
            {canAccess(role, type, category, 'tpvs') && (
              <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'modules') && (
              <NavLinkItem to="/modules" icon={LayoutGrid}>Módulos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'matrixs') && (
              <NavLinkItem to="/matrixs" icon={Download}>Versiones</NavLinkItem>
            )}
            {canAccess(role, type, category, 'lowbluetooth') && (
              <NavLinkItem to="/lowbluetooth" icon={Wifi}>Bluetooth</NavLinkItem>
            )}
            {canAccess(role, type, category, 'lockersqty') && (
              <NavLinkItem to="/lockersqty" icon={BarChart2}>Recuréncia</NavLinkItem>
            )}
            {canAccess(role, type, category, 'openings') && (
              <NavLinkItem to="/openings" icon={DoorOpen}>Aperturas</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "sat" && (
          <div className="submenu-content">
            <div className="submenu-label">SAT</div>
            {canAccess(role, type, category, 'supporttickets') && (
              <NavLinkItem to="/supporttickets" icon={Receipt}>Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'incidences') && (
              <NavLinkItem to="/incidences" icon={SlidersHorizontal}>Incidéncias</NavLinkItem>
            )}
            {canAccess(role, type, category, 'faults') && (
              <NavLinkItem to="/faults" icon={Info}>Averías</NavLinkItem>
            )}
            {canAccess(role, type, category, 'replacements') && (
              <NavLinkItem to="/replacements" icon={SlidersHorizontal}>Recambios</NavLinkItem>
            )}
            {canAccess(role, type, category, 'slas') && (
              <NavLinkItem to="/slas" icon={ShieldCheck}>SLA</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "baqueira" && (
          <div className="submenu-content">
            <div className="submenu-label">Baqueira</div>
            {canAccess(role, type, category, 'operators') && (
              <NavLinkItem to="/operators" icon={Route}>Agregadores</NavLinkItem>
            )}
            {canAccess(role, type, category, 'rules') && (
              <NavLinkItem to="/rules" icon={PencilRuler}>Reglas de negocio</NavLinkItem>
            )}
            {canAccess(role, type, category, 'articles') && (
              <NavLinkItem to="/articles" icon={Newspaper}>Artículos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'arqueosbaqueira') && (
              <NavLinkItem to="/arqueosbaqueira" icon={Gem}>Arqueos ERP Baqueira</NavLinkItem>
            )}
            {canAccess(role, type, category, 'montarto') && (
              <NavLinkItem to="/hotelrental" icon={Hotel}>Montarto</NavLinkItem>
            )}
          </div>
        )}

        {activeMenu === "soporte" && (
          <div className="submenu-content">
            <div className="submenu-label">Soporte</div>
            {canAccess(role, type, category, 'supportticketadd') && (
              <NavLinkItem to="/supportticketadd" icon={Receipt}>Mis Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'support-chat') && (
              <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
            )}
            {canAccess(role, type, category, 'faqs') && (
              <>
                <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
                <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
              </>
            )}
          </div>
        )}

        {activeMenu === "admin" && (
          <div className="submenu-content">
            <div className="submenu-label">Administración</div>
            {canAccess(role, type, category, 'users') && (
              <NavLinkItem to="/users" icon={UserCog}>Administradores</NavLinkItem>
            )}
            {canAccess(role, type, category, 'gameranking') && (
              <NavLinkItem to="/gameranking" icon={Gamepad2}>Juegos</NavLinkItem>
            )}
          </div>
        )}
      </div>

      {/* Pro License Toast */}
      {proToast && (
        <div className="pro-toast">
          <Lock size={16} />
          <span>Esta función solo está disponible con la <strong>Licencia Pro</strong></span>
        </div>
      )}
    </div>
  );
};

/**
 * Right-side Nightscan Drawer
 */
const NightscanDrawer = ({
  open, onClose, issues, onOpenMachine
}) => {
  return (
    <>
      <aside className={`rightpanel ${open ? "rightpanel--open" : ""}`}>
        <div className="rightpanel__header">
          <div className="rightpanel__title">Incidencias Nightscan</div>
          <button className="btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
        <div className="rightpanel__body">
          {issues.length === 0 && (
            <div className="rightpanel__empty">Sin incidencias pendientes.</div>
          )}
          {issues.map((machine) => {
            const date = formatNightscanDate(machine.NightScanTimeStampEnd)?.toLocaleString();
            const status = getMachineStatus(machine.Status, machine.Last_signal);
            const color = getMachineStatusColor(machine.Status, machine.Last_signal);
            return (
              <button
                key={machine._id}
                className="rightpanel__item"
                onClick={() => onOpenMachine(machine._id)}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <strong>{machine.Serial}</strong>
                  <span className="status-badge" style={{ background: color }}>{status}</span>
                </div>
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  <div><small>Cliente:</small> {machine.Customer?.Fullname}</div>
                  <div><small>Noche:</small> {date}</div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>
      {open && <div className="backdrop-right" onClick={onClose} />}
    </>
  );
};

/**
 * Mobile Menu Modal - displays navigation items in a bottom sheet
 */
const MobileMenuModal = ({
  open,
  onClose,
  title,
  icon: Icon,
  children,
  isProfile = false,
  fullname,
  role
}) => {
  return (
    <>
      {open && <div className="backdrop" onClick={onClose} />}
      <div className={`mobile-menu-modal ${open ? "open" : ""}`}>
        {isProfile ? (
          <div className="mobile-profile-header">
            <div className="mobile-profile-avatar">
              {role === 'ADMIN' ? (
                <img
                  src={`https://vacway-images.s3.eu-central-1.amazonaws.com/${fullname}.png`}
                  alt={fullname}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://vacway-images.s3.eu-central-1.amazonaws.com/default.png";
                  }}
                />
              ) : (
                <UserIcon size={24} />
              )}
            </div>
            <div className="mobile-profile-info">
              <div className="mobile-profile-name">{fullname}</div>
              <div className="mobile-profile-role">{role}</div>
            </div>
          </div>
        ) : (
          <div className="mobile-menu-header">
            <div className="mobile-menu-title">
              {Icon && <Icon size={22} />}
              {title}
            </div>
            <button className="mobile-menu-close" onClick={onClose} aria-label="Cerrar menú">
              <ArrowLeft size={22} />
            </button>
          </div>
        )}
        <div className="mobile-menu-content">
          {children}
        </div>
      </div>
    </>
  );
};

/**
 * Mobile Bottom Navigation Bar
 */
const MobileBottomNav = ({
  role,
  type,
  category,
  onNavigate,
  activeTab,
  setActiveTab,
  nightscanCount,
  openTicketsCount,
  openIncidencesCount,
  fullname,
  onLogout
}) => {
  // ============================================
  // CONFIGURATION: Define which sections appear in bottom bar vs "More" menu
  // ============================================
  // Set favoriteMenus to the sections you want in the bottom bar (max 3 recommended)
  // Available options: 'canales', 'gestion', 'reporte', 'operativa', 'sat', 'baqueira', 'soporte', 'admin'
  // Note: 'profile' is always shown as the last tab
  const favoriteMenus = ['canales', 'gestion', 'reporte', 'operativa']; // <-- EDIT THIS to change favorites

  // Check permissions
  const hasCanales = hasAccessToAny(role, type, category, ['machines', 'qrs', 'pdas']);
  const hasGestion = hasAccessToAny(role, type, category, ['lockersale', 'pin', 'redeemcodes', 'rentals', 'calendars', 'partners', 'discount-codes']);
  const hasReporte = hasAccessToAny(role, type, category, ['sales', 'internalsales', 'arqueos', 'arqueossales', 'ocupation', 'objectives', 'ocupationaccumulated', 'ocupationdaily', 'pinrecovers']);
  const hasOperativa = hasAccessToAny(role, type, category, ['customers', 'contracts', 'withdrawals', 'audit', 'tpvs', 'modules', 'matrixs', 'lowbluetooth', 'lockersqty', 'openings']);
  const hasSAT = hasAccessToAny(role, type, category, ['supporttickets', 'incidences', 'faults', 'replacements', 'slas']);
  const hasBaqueira = hasAccessToAny(role, type, category, ['operators', 'rules', 'articles', 'arqueosbaqueira', 'montarto']);
  const hasSoporte = hasAccessToAny(role, type, category, ['supportticketadd', 'faqs', 'manuals', 'support-chat']);
  const hasAdmin = hasAccessToAny(role, type, category, ['users', 'gameranking']);
  const showNightscan = canAccess(role, type, category, 'nightscan');
  const showTicketsIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT1');
  const showIncidencesIndicator = role === 'ADMIN' && (type === 'MASTER' || type === 'SAT2');

  // Menu sections configuration
  const menuSections = {
    canales: {
      has: hasCanales,
      icon: Monitor,
      label: 'Canales',
      isFavorite: favoriteMenus.includes('canales')
    },
    gestion: {
      has: hasGestion,
      icon: CalendarCheck2,
      label: 'Gestión',
      isFavorite: favoriteMenus.includes('gestion')
    },
    reporte: {
      has: hasReporte,
      icon: BarChart2,
      label: 'Reporte',
      isFavorite: favoriteMenus.includes('reporte')
    },
    operativa: {
      has: hasOperativa,
      icon: SlidersHorizontal,
      label: 'Operativa',
      isFavorite: favoriteMenus.includes('operativa')
    },
    sat: {
      has: hasSAT,
      icon: Headset,
      label: 'SAT',
      isFavorite: favoriteMenus.includes('sat')
    },
    baqueira: {
      has: hasBaqueira,
      icon: Mountain,
      label: 'Baqueira',
      isFavorite: favoriteMenus.includes('baqueira')
    },
    soporte: {
      has: hasSoporte,
      icon: HelpCircle,
      label: 'Soporte',
      isFavorite: favoriteMenus.includes('soporte')
    },
    admin: {
      has: hasAdmin,
      icon: Crown,
      label: 'Admin',
      isFavorite: favoriteMenus.includes('admin')
    }
  };

  // Split menus into favorites (shown in bottom bar) and others (shown in "More")
  const favoriteTabs = Object.entries(menuSections)
    .filter(([key, config]) => config.has && config.isFavorite)
    .map(([key]) => key);

  const moreTabs = Object.entries(menuSections)
    .filter(([key, config]) => config.has && !config.isFavorite)
    .map(([key]) => key);

  const showMoreButton = moreTabs.length > 0;

  // Pro-lock toast state
  const [proToast, setProToast] = React.useState(false);
  const proToastTimer = React.useRef(null);
  const showProToast = () => {
    setProToast(true);
    if (proToastTimer.current) clearTimeout(proToastTimer.current);
    proToastTimer.current = setTimeout(() => setProToast(false), 3500);
  };

  const NavLinkItem = ({ to, icon: Icon, children }) => {
    const feature = to.replace(/^\//, '');
    const locked = isFeatureLocked(role, type, category, feature);

    if (locked) {
      return (
        <div
          className="mobile-menu-item mobile-menu-item--locked"
          onClick={(e) => { e.preventDefault(); showProToast(); }}
          role="button"
          tabIndex={0}
        >
          {Icon && <Icon size={20} />}
          <span>{children}</span>
          <span className="pro-badge-inline">PRO</span>
          <Lock size={14} className="lock-icon-inline" />
        </div>
      );
    }

    return (
      <RRNavLink
        to={to}
        className="mobile-menu-item"
        onClick={() => {
          setActiveTab(null);
          onNavigate();
        }}
      >
        {Icon && <Icon size={20} />}
        <span>{children}</span>
      </RRNavLink>
    );
  };

  return (
    <>
      <nav className="mobile-bottom-nav">
        {/* Favorite tabs (shown directly in bottom bar) */}
        {favoriteTabs.map(tabKey => {
          const config = menuSections[tabKey];
          const Icon = config.icon;
          return (
            <button
              key={tabKey}
              className={`mobile-tab ${activeTab === tabKey ? "active" : ""}`}
              onClick={() => setActiveTab(prev => prev === tabKey ? null : tabKey)}
              aria-label={config.label}
            >
              <Icon size={22} />
              <span className="mobile-tab-label">{config.label}</span>
            </button>
          );
        })}

        {/* More button (contains non-favorite sections) */}
        {showMoreButton && (
          <button
            className={`mobile-tab ${activeTab === "more" ? "active" : ""}`}
            onClick={() => setActiveTab(prev => prev === "more" ? null : "more")}
            aria-label="Más opciones"
          >
            <LayoutGrid size={22} />
            <span className="mobile-tab-label">Más</span>
          </button>
        )}

        {/* Profile tab (always shown) */}
        <button
          className={`mobile-tab ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => setActiveTab(prev => prev === "profile" ? null : "profile")}
          aria-label="Perfil"
        >
          {(() => {
            const totalBadgeCount = 
              (showNightscan ? nightscanCount : 0) + 
              (showTicketsIndicator ? openTicketsCount : 0) + 
              (showIncidencesIndicator ? openIncidencesCount : 0);
            return totalBadgeCount > 0 && (
              <span className="mobile-tab-badge">{totalBadgeCount}</span>
            );
          })()}
          <UserIcon size={22} />
          <span className="mobile-tab-label">Perfil</span>
        </button>
      </nav>

      {/* Modals for each tab */}
      <MobileMenuModal
        open={activeTab === "canales"}
        onClose={() => setActiveTab(null)}
        title="Canales"
        icon={Monitor}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Canales de venta</div>
          {canAccess(role, type, category, 'machines') && (
            <NavLinkItem to="/machines" icon={Monitor}>
              {category === 'BAQUEIRA' ? 'Controladores' : 'Kioscos'}
            </NavLinkItem>
          )}
          {canAccess(role, type, category, 'qrs') && (
            <NavLinkItem to="/qrs" icon={QrCode}>QRs</NavLinkItem>
          )}
          {canAccess(role, type, category, 'pdas') && (
            <NavLinkItem to="/pdas" icon={Smartphone}>PDAs</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "gestion"}
        onClose={() => setActiveTab(null)}
        title="Gestión"
        icon={CalendarCheck2}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Gestión</div>
          {canAccess(role, type, category, 'lockersale') && (
            <NavLinkItem to="/lockersale" icon={CreditCard}>Vender</NavLinkItem>
          )}
          {canAccess(role, type, category, 'pin') && (
            <NavLinkItem to="/pin" icon={Unlock}>Recuperar Pin</NavLinkItem>
          )}
          {canAccess(role, type, category, 'redeemcodes') && (
            <NavLinkItem to="/redeemcodes" icon={Ticket}>Códigos Canjeables</NavLinkItem>
          )}
          {canAccess(role, type, category, 'rentals') && (
            <NavLinkItem to="/rentals" icon={Key}>Reservas</NavLinkItem>
          )}
          {canAccess(role, type, category, 'calendars') && (
            <NavLinkItem to="/calendars" icon={CalendarCheck2}>Calendarios</NavLinkItem>
          )}
          {canAccess(role, type, category, 'email-templates') && (
            <NavLinkItem to="/email-templates" icon={Mail}>Plantillas Email</NavLinkItem>
          )}
          {canAccess(role, type, category, 'partners') && (
            <NavLinkItem to="/partners" icon={UserCog}>Partners</NavLinkItem>
          )}
          {canAccess(role, type, category, 'discount-codes') && (
            <NavLinkItem to="/discount-codes" icon={Tag}>Códigos Descuento</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "reporte"}
        onClose={() => setActiveTab(null)}
        title="Reporte"
        icon={BarChart2}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Reporte</div>
          {canAccess(role, type, category, 'sales') && (
            <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
          )}
          {canAccess(role, type, category, 'internalsales') && (
            <NavLinkItem to="/internalsales" icon={Star}>Análisis por tipo</NavLinkItem>
          )}
          {canAccess(role, type, category, 'arqueos') && (
            <>
              <NavLinkItem to="/arqueos" icon={BarChart2}>Gráficos</NavLinkItem>
              <NavLinkItem to="/arqueossales" icon={PieChart}>KPI</NavLinkItem>
            </>
          )}
          {canAccess(role, type, category, 'ocupation') && (
            <NavLinkItem to="/ocupation" icon={BarChart2}>Ocupación</NavLinkItem>
          )}
          {canAccess(role, type, category, 'objectives') && (
            <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
          )}
          {canAccess(role, type, category, 'ocupationaccumulated') && (
            <>
              <NavLinkItem to="/ocupationaccumulated" icon={BarChart2}>Ocupación acumulada</NavLinkItem>
              <NavLinkItem to="/ocupationdaily" icon={BarChart2}>Ocupación diaria</NavLinkItem>
            </>
          )}
          {canAccess(role, type, category, 'pinrecovers') && (
            <NavLinkItem to="/pinrecovers" icon={Lock}>Recuperación pines</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "operativa"}
        onClose={() => setActiveTab(null)}
        title="Operativa"
        icon={SlidersHorizontal}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Operativa</div>
          {canAccess(role, type, category, 'customers') && (
            <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
          )}
          {canAccess(role, type, category, 'contracts') && (
            <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
          )}
          {canAccess(role, type, category, 'withdrawals') && (
            <NavLinkItem to="/withdrawals" icon={Calculator}>Retiros</NavLinkItem>
          )}
          {canAccess(role, type, category, 'audit') && (
            <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
          )}
          {canAccess(role, type, category, 'tpvs') && (
            <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
          )}
          {canAccess(role, type, category, 'modules') && (
            <NavLinkItem to="/modules" icon={LayoutGrid}>Módulos</NavLinkItem>
          )}
          {canAccess(role, type, category, 'matrixs') && (
            <NavLinkItem to="/matrixs" icon={Download}>Versiones</NavLinkItem>
          )}
          {canAccess(role, type, category, 'lowbluetooth') && (
            <NavLinkItem to="/lowbluetooth" icon={Wifi}>Bluetooth</NavLinkItem>
          )}
          {canAccess(role, type, category, 'lockersqty') && (
            <NavLinkItem to="/lockersqty" icon={BarChart2}>Recuréncia</NavLinkItem>
          )}
          {canAccess(role, type, category, 'openings') && (
            <NavLinkItem to="/openings" icon={DoorOpen}>Aperturas</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "sat"}
        onClose={() => setActiveTab(null)}
        title="SAT"
        icon={Headset}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">SAT</div>
          {canAccess(role, type, category, 'supporttickets') && (
            <NavLinkItem to="/supporttickets" icon={Receipt}>Tickets</NavLinkItem>
          )}
          {canAccess(role, type, category, 'incidences') && (
            <NavLinkItem to="/incidences" icon={SlidersHorizontal}>Incidéncias</NavLinkItem>
          )}
          {canAccess(role, type, category, 'faults') && (
            <NavLinkItem to="/faults" icon={Info}>Averías</NavLinkItem>
          )}
          {canAccess(role, type, category, 'replacements') && (
            <NavLinkItem to="/replacements" icon={SlidersHorizontal}>Recambios</NavLinkItem>
          )}
          {canAccess(role, type, category, 'slas') && (
            <NavLinkItem to="/slas" icon={ShieldCheck}>SLA</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "baqueira"}
        onClose={() => setActiveTab(null)}
        title="Baqueira"
        icon={Mountain}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Baqueira</div>
          {canAccess(role, type, category, 'operators') && (
            <NavLinkItem to="/operators" icon={Route}>Agregadores</NavLinkItem>
          )}
          {canAccess(role, type, category, 'rules') && (
            <NavLinkItem to="/rules" icon={PencilRuler}>Reglas de negocio</NavLinkItem>
          )}
          {canAccess(role, type, category, 'articles') && (
            <NavLinkItem to="/articles" icon={Newspaper}>Artículos</NavLinkItem>
          )}
          {canAccess(role, type, category, 'arqueosbaqueira') && (
            <NavLinkItem to="/arqueosbaqueira" icon={Gem}>Arqueos ERP Baqueira</NavLinkItem>
          )}
          {canAccess(role, type, category, 'montarto') && (
            <NavLinkItem to="/hotelrental" icon={Hotel}>Montarto</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "soporte"}
        onClose={() => setActiveTab(null)}
        title="Soporte"
        icon={HelpCircle}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Soporte</div>
          {canAccess(role, type, category, 'supportticketadd') && (
            <NavLinkItem to="/supportticketadd" icon={Receipt}>Mis Tickets</NavLinkItem>
          )}
          {canAccess(role, type, category, 'support-chat') && (
            <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
          )}
          {canAccess(role, type, category, 'faqs') && (
            <>
              <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
              <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
            </>
          )}
        </div>
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "admin"}
        onClose={() => setActiveTab(null)}
        title="Administración"
        icon={Crown}
      >
        <div className="mobile-menu-section">
          <div className="mobile-menu-section-title">Administración</div>
          {canAccess(role, type, category, 'users') && (
            <NavLinkItem to="/users" icon={UserCog}>Administradores</NavLinkItem>
          )}
          {canAccess(role, type, category, 'gameranking') && (
            <NavLinkItem to="/gameranking" icon={Gamepad2}>Juegos</NavLinkItem>
          )}
        </div>
      </MobileMenuModal>

      {/* More modal - dynamically shows non-favorite sections */}
      <MobileMenuModal
        open={activeTab === "more"}
        onClose={() => setActiveTab(null)}
        title="Más opciones"
        icon={LayoutGrid}
      >
        {/* Show each non-favorite section that user has access to */}
        {moreTabs.includes('canales') && hasCanales && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Canales de venta</div>
            {canAccess(role, type, category, 'machines') && (
              <NavLinkItem to="/machines" icon={Monitor}>
                {category === 'BAQUEIRA' ? 'Controladores' : 'Kioscos'}
              </NavLinkItem>
            )}
            {canAccess(role, type, category, 'qrs') && (
              <NavLinkItem to="/qrs" icon={QrCode}>QRs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'pdas') && (
              <NavLinkItem to="/pdas" icon={Smartphone}>PDAs</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('gestion') && hasGestion && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Gestión</div>
            {canAccess(role, type, category, 'lockersale') && (
              <NavLinkItem to="/lockersale" icon={CreditCard}>Vender</NavLinkItem>
            )}
            {canAccess(role, type, category, 'pin') && (
              <NavLinkItem to="/pin" icon={Unlock}>Recuperar Pin</NavLinkItem>
            )}
            {canAccess(role, type, category, 'redeemcodes') && (
              <NavLinkItem to="/redeemcodes" icon={Ticket}>Códigos Canjeables</NavLinkItem>
            )}
            {canAccess(role, type, category, 'rentals') && (
              <NavLinkItem to="/rentals" icon={Key}>Reservas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'calendars') && (
              <NavLinkItem to="/calendars" icon={CalendarCheck2}>Calendarios</NavLinkItem>
            )}
            {canAccess(role, type, category, 'email-templates') && (
              <NavLinkItem to="/email-templates" icon={Mail}>Plantillas Email</NavLinkItem>
            )}
            {canAccess(role, type, category, 'partners') && (
              <NavLinkItem to="/partners" icon={UserCog}>Partners</NavLinkItem>
            )}
            {canAccess(role, type, category, 'discount-codes') && (
              <NavLinkItem to="/discount-codes" icon={Tag}>Códigos Descuento</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('reporte') && hasReporte && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Reporte</div>
            {canAccess(role, type, category, 'sales') && (
              <NavLinkItem to="/sales" icon={Filter}>Ventas</NavLinkItem>
            )}
            {canAccess(role, type, category, 'internalsales') && (
              <NavLinkItem to="/internalsales" icon={Star}>Análisis por tipo</NavLinkItem>
            )}
            {canAccess(role, type, category, 'arqueos') && (
              <>
                <NavLinkItem to="/arqueos" icon={BarChart2}>Gráficos</NavLinkItem>
                <NavLinkItem to="/arqueossales" icon={PieChart}>KPI</NavLinkItem>
              </>
            )}
            {canAccess(role, type, category, 'ocupation') && (
              <NavLinkItem to="/ocupation" icon={BarChart2}>Ocupación</NavLinkItem>
            )}
            {canAccess(role, type, category, 'objectives') && (
              <NavLinkItem to="/objectives" icon={Target}>Objetivos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'ocupationaccumulated') && (
              <>
                <NavLinkItem to="/ocupationaccumulated" icon={BarChart2}>Ocupación acumulada</NavLinkItem>
                <NavLinkItem to="/ocupationdaily" icon={BarChart2}>Ocupación diaria</NavLinkItem>
              </>
            )}
            {canAccess(role, type, category, 'pinrecovers') && (
              <NavLinkItem to="/pinrecovers" icon={Lock}>Recuperación pines</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('operativa') && hasOperativa && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Operativa</div>
            {canAccess(role, type, category, 'customers') && (
              <NavLinkItem to="/customers" icon={Crown}>Clientes</NavLinkItem>
            )}
            {canAccess(role, type, category, 'contracts') && (
              <NavLinkItem to="/contracts" icon={FileSignature}>Contratos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'withdrawals') && (
              <NavLinkItem to="/withdrawals" icon={Calculator}>Retiros</NavLinkItem>
            )}
            {canAccess(role, type, category, 'audit') && (
              <NavLinkItem to="/audit" icon={Database}>Audit</NavLinkItem>
            )}
            {canAccess(role, type, category, 'tpvs') && (
              <NavLinkItem to="/tpvs" icon={Smartphone}>TPVs</NavLinkItem>
            )}
            {canAccess(role, type, category, 'modules') && (
              <NavLinkItem to="/modules" icon={LayoutGrid}>Módulos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'matrixs') && (
              <NavLinkItem to="/matrixs" icon={Download}>Versiones</NavLinkItem>
            )}
            {canAccess(role, type, category, 'lowbluetooth') && (
              <NavLinkItem to="/lowbluetooth" icon={Wifi}>Bluetooth</NavLinkItem>
            )}
            {canAccess(role, type, category, 'lockersqty') && (
              <NavLinkItem to="/lockersqty" icon={BarChart2}>Recuréncia</NavLinkItem>
            )}
            {canAccess(role, type, category, 'openings') && (
              <NavLinkItem to="/openings" icon={DoorOpen}>Aperturas</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('sat') && hasSAT && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">SAT</div>
            {canAccess(role, type, category, 'supporttickets') && (
              <NavLinkItem to="/supporttickets" icon={Receipt}>Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'incidences') && (
              <NavLinkItem to="/incidences" icon={SlidersHorizontal}>Incidéncias</NavLinkItem>
            )}
            {canAccess(role, type, category, 'faults') && (
              <NavLinkItem to="/faults" icon={Info}>Averías</NavLinkItem>
            )}
            {canAccess(role, type, category, 'replacements') && (
              <NavLinkItem to="/replacements" icon={SlidersHorizontal}>Recambios</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('baqueira') && hasBaqueira && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Baqueira</div>
            {canAccess(role, type, category, 'operators') && (
              <NavLinkItem to="/operators" icon={Route}>Agregadores</NavLinkItem>
            )}
            {canAccess(role, type, category, 'rules') && (
              <NavLinkItem to="/rules" icon={PencilRuler}>Reglas de negocio</NavLinkItem>
            )}
            {canAccess(role, type, category, 'articles') && (
              <NavLinkItem to="/articles" icon={Newspaper}>Artículos</NavLinkItem>
            )}
            {canAccess(role, type, category, 'arqueosbaqueira') && (
              <NavLinkItem to="/arqueosbaqueira" icon={Gem}>Arqueos ERP Baqueira</NavLinkItem>
            )}
            {canAccess(role, type, category, 'montarto') && (
              <NavLinkItem to="/hotelrental" icon={Hotel}>Montarto</NavLinkItem>
            )}
          </div>
        )}

        {moreTabs.includes('soporte') && hasSoporte && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Soporte</div>
            {canAccess(role, type, category, 'supportticketadd') && (
              <NavLinkItem to="/supportticketadd" icon={Receipt}>Mis Tickets</NavLinkItem>
            )}
            {canAccess(role, type, category, 'support-chat') && (
              <NavLinkItem to="/support-chat" icon={MessageSquare}>Chat</NavLinkItem>
            )}
            {canAccess(role, type, category, 'faqs') && (
              <>
                <NavLinkItem to="/faqs" icon={HelpCircle}>FAQs</NavLinkItem>
                <NavLinkItem to="/manuals" icon={BookOpen}>Manuales</NavLinkItem>
              </>
            )}
          </div>
        )}

        {moreTabs.includes('admin') && hasAdmin && (
          <div className="mobile-menu-section">
            <div className="mobile-menu-section-title">Administración</div>
            {canAccess(role, type, category, 'users') && (
              <NavLinkItem to="/users" icon={UserCog}>Administradores</NavLinkItem>
            )}
            {canAccess(role, type, category, 'gameranking') && (
              <NavLinkItem to="/gameranking" icon={Gamepad2}>Juegos</NavLinkItem>
            )}
          </div>
        )}
      </MobileMenuModal>

      <MobileMenuModal
        open={activeTab === "profile"}
        onClose={() => setActiveTab(null)}
        title="Perfil"
        isProfile={true}
        fullname={fullname}
        role={role}
      >
        <div className="mobile-menu-section">
          {/* Nightscan, Tickets, Incidencias - always show if user has access */}
          {(showNightscan || showTicketsIndicator || showIncidencesIndicator) && (
            <>
              <div className="mobile-menu-section-title">Notificaciones</div>
              
              {showNightscan && (
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    setActiveTab(null);
                    onNavigate('nightscan');
                  }}
                >
                  <Info size={20} />
                  <span>Incidencias Nightscan</span>
                  {nightscanCount > 0 && (
                    <span className="mobile-tab-badge" style={{ position: 'static', marginLeft: 'auto' }}>
                      {nightscanCount}
                    </span>
                  )}
                </button>
              )}
              
              {showTicketsIndicator && (
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    setActiveTab(null);
                    onNavigate('/supporttickets');
                  }}
                >
                  <Ticket size={20} />
                  <span>Tickets de Soporte</span>
                  {openTicketsCount > 0 && (
                    <span className="mobile-tab-badge" style={{ position: 'static', marginLeft: 'auto', background: '#3b82f6' }}>
                      {openTicketsCount}
                    </span>
                  )}
                </button>
              )}
              
              {showIncidencesIndicator && (
                <button
                  className="mobile-menu-item"
                  onClick={() => {
                    setActiveTab(null);
                    onNavigate('/incidences');
                  }}
                >
                  <AlertTriangle size={20} />
                  <span>Incidencias</span>
                  {openIncidencesCount > 0 && (
                    <span className="mobile-tab-badge" style={{ position: 'static', marginLeft: 'auto', background: '#f97316' }}>
                      {openIncidencesCount}
                    </span>
                  )}
                </button>
              )}
              
              <div className="mobile-menu-divider" />
            </>
          )}

          <div className="mobile-menu-section-title">Configuración</div>

          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <SlidersHorizontal size={20} style={{ color: 'var(--sidebar-icon-color)' }} />
              <span style={{ color: 'var(--sidebar-text-secondary)', fontSize: 15 }}>Tema</span>
            </div>
            <DarkModeToggle />
          </div>

          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <LayoutGrid size={20} style={{ color: 'var(--sidebar-icon-color)' }} />
              <span style={{ color: 'var(--sidebar-text-secondary)', fontSize: 15 }}>Idioma</span>
            </div>
            <div style={{ position: 'relative', zIndex: 20 }}>
              <GoogleTranslate />
            </div>
          </div>

          <div className="mobile-menu-divider" />

          <button className="mobile-menu-item" onClick={onLogout}>
            <LogOut size={20} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </MobileMenuModal>

      {/* Pro License Toast */}
      {proToast && (
        <div className="pro-toast pro-toast--mobile">
          <Lock size={16} />
          <span>Esta función solo está disponible con la <strong>Licencia Pro</strong></span>
        </div>
      )}
    </>
  );
};

/**
 * AppShell
 */
const AppShellBase = ({
  children,
  fullname,
  role,
  type,
  category,
  authLogout,
  fetchMachinesNightscan,
  fetchOpenTicketsCount,
  fetchOpenIncidencesCount,
  machinesNightscan,
  openTicketsCount,
  openIncidencesCount,
  push,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);

  // NEW: right panel Nightscan state
  const [nightscanOpen, setNightscanOpen] = useState(false);

  // NEW: Command Palette state
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  // NEW: Mobile bottom nav tab state
  const [mobileActiveTab, setMobileActiveTab] = useState(null);

  // NEW: Minesweeper modal state
  const [minesweeperOpen, setMinesweeperOpen] = useState(false);

  // Handle user button click - show Minesweeper for ADMIN users
  const handleUserClick = () => {
    if (role === 'ADMIN') {
      setMinesweeperOpen(true);
    }
  };

  useEffect(() => { injectStyles(); }, []);

  // Command Palette keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Viewport watcher
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
    return () => {
      mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
    };
  }, []);

  // Collapse the left menu ALWAYS after any navigation
  const onNavigate = (action) => {
    setCollapsed(true);        // collapse hamburger after navigating
    setNightscanOpen(false);   // optional: also close right panel
    if (isMobile) {
      setMobileOpen(false);
      setMobileActiveTab(null); // close mobile bottom nav modals

      // Handle special actions from mobile menu
      if (action === 'nightscan') {
        setNightscanOpen(true);
      } else if (action && action.startsWith('/')) {
        // Navigate to a route
        push(action);
      }
    }
  };

  // Open machine from the right drawer -> also collapse left
  const openMachine = (id) => {
    push(`/machines/${id}`);
    setNightscanOpen(false);
    setMobileOpen(false);
    setCollapsed(true);      // ensure left menu collapsed
  };

  // Initial load / auth rule and fetch
  useEffect(() => {
    if (fullname === 'Joaquin Abellan') {
      authLogout();
      push('/login');
    }
    
    // Fetch ticket/incidence counts based on user type
    if (role === 'ADMIN') {
      fetchMachinesNightscan && fetchMachinesNightscan();

      if (type === 'MASTER' || type === 'SAT1') {
        fetchOpenTicketsCount && fetchOpenTicketsCount();
      }
      if (type === 'MASTER' || type === 'SAT2') {
        fetchOpenIncidencesCount && fetchOpenIncidencesCount();
      }
    }
  }, [fullname, authLogout, push, fetchMachinesNightscan, fetchOpenTicketsCount, fetchOpenIncidencesCount, role, type]);

  //useEffect(() => { localStorage.setItem("sidebarCollapsed", collapsed ? "1" : "0"); }, [collapsed]);

  useEffect(() => {
    if (isMobile && nightscanOpen && mobileOpen) {
      setMobileOpen(false);
    }
  }, [isMobile, nightscanOpen, mobileOpen]);

  // Compute issues with same rules as legacy (exclude serials + sleep period)
  const nightscanIssues = useMemo(() => {
    const excludedSerials = ["SHOWROOM-I", "SHOWROOM-O", "VLK00000", "VLK00001"];
    const list = (machinesNightscan || []).filter(machine => {
      const date = formatNightscanDate(machine.NightScanTimeStampEnd);
      const notSleeping = !isNightscanInSleepPeriod(date, machine.SleepStart, machine.SleepEnd);
      const notExcluded = !excludedSerials.includes(machine.Serial);
      return notSleeping && notExcluded;
    });
    // Sort newest first (optional)
    list.sort((a, b) => {
      const da = formatNightscanDate(a.NightScanTimeStampEnd).getTime();
      const db = formatNightscanDate(b.NightScanTimeStampEnd).getTime();
      return db - da;
    });
    return list;
  }, [machinesNightscan]);

  const nightscanCount = nightscanIssues.length;

  const onLogout = () => { authLogout(); push('/login'); };

  // Open/close the RIGHT Nightscan drawer; if opening, collapse/close LEFT menu
  const handleToggleNightscan = () => {
    setNightscanOpen(prev => {
      const next = !prev;
      if (next) {
        // Ensure LEFT is closed when RIGHT opens
        if (isMobile) {
          setMobileOpen(false);     // close mobile left drawer
        } else {
          setCollapsed(true);       // collapse left on desktop
        }
      }
      return next;
    });
  };

  // Toggle LEFT on mobile; if opening, close RIGHT
  const handleToggleSidebarMobile = () => {
    setMobileOpen(prev => {
      const next = !prev;
      if (next) {
        setNightscanOpen(false);    // close right when left opens
      }
      return next;
    });
  };

  // Toggle LEFT on desktop; when expanding (not collapsed), close RIGHT
  const handleToggleDesktopSidebar = () => {
    setCollapsed(prev => {
      const next = !prev;           // next=false means expanded; next=true means collapsed
      if (!isMobile && !next) {
        setNightscanOpen(false);    // expanded left => close right
      }
      return next;
    });
  };



  return (
    <ToastProvider>
      <div className="shell">
        <TopBar
          fullname={fullname}
          onLogout={onLogout}
          isMobile={isMobile}
          role={role}
          type={type}
          category={category}
          nightscanCount={nightscanCount}
          openTicketsCount={openTicketsCount}
          openIncidencesCount={openIncidencesCount}
          onToggleNightscan={handleToggleNightscan}
          onToggleSidebarMobile={handleToggleSidebarMobile}
          onOpenCommandPalette={() => setCommandPaletteOpen(true)}
          onUserClick={handleUserClick}
          push={push}
        />

        {/* Command Palette */}
        <CommandPalette
          isOpen={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          role={role}
          type={type}
          category={category}
          canAccess={canAccess}
          hasAccessToAny={hasAccessToAny}
          push={push}
        />

        {/* === Desktop Sidebar === */}
        {!isMobile && (
          <div className="sidebar-wrapper">
            <Sidebar
              role={role}
              type={type}
              category={category}
              onNavigate={onNavigate}
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
            />
          </div>
        )}

        <main className={`content ${!isMobile ? (activeMenu ? 'shifted' : 'not-shifted') : ''}`}>
          {/* Breadcrumbs */}
          <Breadcrumbs category={category} />

          {children}
        </main>

        {/* Mobile Bottom Navigation */}
        {isMobile && (
          <MobileBottomNav
            role={role}
            type={type}
            category={category}
            onNavigate={onNavigate}
            activeTab={mobileActiveTab}
            setActiveTab={setMobileActiveTab}
            nightscanCount={nightscanCount}
            openTicketsCount={openTicketsCount}
            openIncidencesCount={openIncidencesCount}
            fullname={fullname}
            onLogout={onLogout}
          />
        )}

        {/* Right-side Nightscan drawer */}
        {role === "ADMIN" && (
          <NightscanDrawer
            open={nightscanOpen}
            onClose={() => setNightscanOpen(false)}
            issues={nightscanIssues}
            onOpenMachine={openMachine}
          />
        )}

        {/* Minesweeper modal for ADMIN users */}
        <Minesweeper
          isOpen={minesweeperOpen}
          toggle={() => setMinesweeperOpen(false)}
        />

        <FloatingToolsButton />
      </div>
    </ToastProvider>
  );
};

const mapStateToProps = (state) => ({
  fullname: state.auth.fullname,
  role: state.auth.role,
  type: state.auth.type,
  category: state.auth.category,
  machinesNightscan: state.machines.machinesNightscan,
  openTicketsCount: state.supporttickets.openTicketsCount,
  openIncidencesCount: state.incidences.openIncidencesCount,
});

const mapDispatchToProps = (dispatch) => ({
  authLogout: bindActionCreators(authLogout, dispatch),
  fetchMachinesNightscan: bindActionCreators(fetchMachinesNightscan, dispatch),
  fetchOpenTicketsCount: bindActionCreators(fetchOpenTicketsCount, dispatch),
  fetchOpenIncidencesCount: bindActionCreators(fetchOpenIncidencesCount, dispatch),
  push: bindActionCreators(push, dispatch),
});

export const AppShell = connect(mapStateToProps, mapDispatchToProps)(AppShellBase);
